import { i18n } from '@/i18n';
import { API, api, compass } from '@resclick/compass';
import { AxiosResponse } from 'axios';
import { classToClass, plainToClass } from 'class-transformer';
import { ClassType } from 'class-transformer/ClassTransformer';
import { validate } from 'class-validator';
import deepEqual from 'deep-equal';
import { debounce, Notify } from 'quasar';
import { VueConstructor } from 'vue';
import { Component, Vue } from 'vue-property-decorator';

export type CrsAction<T> = T & {
  dto: Extract<T, { dto?: any }>['dto'];
  params: Extract<T, { params?: any }>['params'];
  response: Extract<T, { response?: any }>['response'];
};

type Dto<N extends keyof API, A extends keyof API[N]> = InstanceType<CrsAction<API[N][A]>['dto']>;
type DtoProp<N extends keyof API, A extends keyof API[N]> = keyof Dto<N, A>;
type ResponseType<N extends keyof API, A extends keyof API[N]> = CrsAction<API[N][A]>['response'];

const ERROR_MAP: { [key: string]: string } = {
  isNotEmpty: 'VALIDATOR.IS_REQUIRED',
  isNumber: 'VALIDATOR.IS_NUMBER',
  isString: 'VALIDATOR.IS_STRING',
  isIn: 'VALIDATOR.IS_IN',
  isEmail: 'VALIDATOR.IS_EMAIL',
  minLength: 'VALIDATOR.MIN_LENGTH_REQUIRED',
  isUniqueProperty: 'VALIDATOR.IS_UNIQUE_PROPERTY',
};

@Component
export class CrsForm<N extends keyof API, A extends keyof API[N]> extends Vue {
  errorStack: any = {};
  dirtyStack: any = {};
  namespace!: N;
  action!: A;
  fields!: InstanceType<CrsAction<API[N][A]>['dto']>;
  initialFields!: CrsAction<API[N][A]>['dto'];
  dto?: CrsAction<API[N][A]>['dto'];
  watchers: any = {};

  get getError() {
    return (prop: DtoProp<N, A> | string) => {
      let result: any = '';
      const errors = this.errorStack[prop];
      if (errors) {
        const key = Object.keys(errors)[0];
        if (key) result = ERROR_MAP[key] ? i18n.t(ERROR_MAP[key], { ...errors[key] }) : key;
      }
      return result;
    };
  }

  registerWatcher(prop: any) {
    const validate = debounce(this.validate, 100);

    if (!this.watchers[prop]) {
      this.$watch('fields.' + prop, async () => {
        this.dirtyStack[prop] = true;
        await validate();
      });

      this.watchers[prop] = true;
    }
  }

  hasError(prop: DtoProp<N, A> | string) {
    this.registerWatcher(prop);
    return !!this.errorStack[prop] && !!this.dirtyStack[prop];
  }

  reset() {
    this.errorStack = {};
    this.dirtyStack = {};
    this.setFields(this.initialFields);
  }

  markAllAsDirty() {
    for (const key of Object.keys(this.initialFields)) {
      this.dirtyStack[key] = true;
    }

    for (const key of Object.keys(this.watchers)) {
      this.dirtyStack[key] = true;
    }
  }

  hasChanges() {
    return !deepEqual(this.fields, plainToClass(this.dto as ClassType<Dto<N, A>>, this.initialFields));
  }

  async submit(options?: {
    params?: CrsAction<API[N][A]> extends { params: any }
      ? { [K in CrsAction<API[N][A]>['params'][number]]: unknown }
      : undefined;
    lang?: string;
  }): Promise<AxiosResponse<ResponseType<N, A>>> {
    this.markAllAsDirty();
    const _params: any = options?.params || {};
    const isValid = await this.validate();
    return new Promise((resolve, reject) => {
      if (isValid) {
        const action = api[this.namespace][this.action] as any;

        if (!options?.params && action && action.params) {
          if (this.fields && Object.keys(action.params).every((key) => Object.keys(this.fields as any).includes(key))) {
            for (const key of Object.keys(action.params)) {
              _params[key] = this.fields[key];
            }
          }
        }

        compass
          .do(this.namespace, this.action, {
            data: this.fields,
            params: _params,
            lang: options?.lang || 'en-US',
          })
          .then((res) => {
            Notify.create({
              color: 'positive',
              icon: 'mdi-cloud-check-outline',
              message: i18n.t('MESSAGE.OPERATION_SUCCESSFUL') as any,
            });
            resolve(res);
          })
          .catch((err) => {
            const { response = {} } = err;
            const { status = 500 } = response;
            reject(err);

            Notify.create({
              color: 'negative',
              icon: 'mdi-alert-circle-outline',
              message: i18n.t('MESSAGE.' + status) as any,
            });
          });
      } else {
        Notify.create({
          color: 'negative',
          icon: 'mdi-alert-circle-outline',
          message: i18n.t('MESSAGE.SOME_FIELDS_ARE_INCORRECT') as any,
        });
        reject('Invalid form fields!');
      }
    });
  }

  async validate() {
    // this.refresh();
    const val = classToClass(this.fields, this.dto);
    const errors = await validate(val);

    this.errorStack = {};

    const normalize = (error: any, parent?: string) => {
      let indexer: any = [];
      if (parent) indexer.push(parent);
      indexer.push(error.property);
      indexer = indexer.join('.');

      if (error.children && error.children.length) {
        error.children.forEach((subItem: any) => {
          normalize(subItem, indexer);
        });
      } else {
        this.errorStack[indexer] = error.contexts || error.constraints;
      }
    };
    for (const error of errors) {
      normalize(error);
    }

    return Object.keys(this.errorStack).length < 1;
  }

  setFields(fields: Dto<N, A>) {
    Vue.set(
      this,
      'fields',
      plainToClass(this.dto as ClassType<Dto<N, A>>, Object.assign({}, this.initialFields, fields)),
    );
    Vue.set(
      this,
      'initialFields',
      plainToClass(this.dto as ClassType<Dto<N, A>>, Object.assign({}, this.initialFields, fields)),
    );
  }

  refresh() {
    Vue.set(
      this,
      'fields',
      plainToClass(this.dto as ClassType<Dto<N, A>>, Object.assign({}, this.initialFields, this.fields)),
    );
  }

  created() {
    this.setFields(this.fields);
  }
}

declare module 'vue/types/vue' {
  interface Vue {
    $crsForm: <N extends keyof API, A extends keyof API[N]>(
      namespace: N,
      action: A,
      fields: Dto<N, A>,
    ) => CrsForm<N, A>;
    $getCrsList: <N extends keyof API, A extends keyof API[N]>(
      namespace: N,
      action: A,
      config: any,
    ) => Promise<ResponseType<N, A>>;
  }
}

const plugin = {
  install(Vue: VueConstructor) {
    Vue.prototype.$crsForm = <N extends keyof API, A extends keyof API[N]>(
      namespace: N,
      action: A,
      fields: InstanceType<CrsAction<API[N][A]>['dto']>,
    ) =>
      new CrsForm({
        data: {
          initialFields: fields,
          fields,
          namespace,
          action,
          get dto() {
            const { dto } = api[namespace][action] as any;
            return dto;
          },
        },
      });

    Vue.prototype.$getCrsList = async <N extends keyof API, A extends keyof API[N]>(
      namespace: N,
      action: A,
      config: any,
    ) => {
      let page = 1;
      const result: any[] = [];
      const { headers, data } = await compass.do(namespace, action, {
        page: 1,
        limit: 100,
        ...config,
      });
      if (Array.isArray(data)) {
        result.push(...(data as unknown[]));
      }

      const postRequests: any = [];
      if (headers && headers['x-pagination-total-count']) {
        while (Number(headers['x-pagination-total-count']) > page * 100) {
          postRequests.push(
            compass.do(namespace, action, {
              page: ++page,
              limit: 100,
              ...config,
            }),
          );
        }
      }

      const rest = await Promise.all(postRequests);
      result.push(...rest.flatMap((r: any) => r.data));

      return Object.freeze(result);
    };
  },
};

export default plugin;
